import React, { useState, useEffect, useMemo } from "react";
import { Box, SvgIcon, Typography } from "@mui/material";
import { ReactComponent as MicrophoneIcon } from "../assets/microphone.svg";
import GuideWaveformOrb, {
  GUIDE_VISUAL_STATES,
  hexToRgbGuide,
} from "./GuideWaveformOrb";
import { normalizeGuideStateValue } from "./unityWebViewer/guideRuntime";
import {
  getViewerActionPaddingX,
  getViewerPanelPaddingSx,
  viewerActionButtonLabelSx,
  viewerActionButtonSx,
  viewerCompactActionPaddingX,
  viewerGhostActionButtonSx,
  viewerPillActionButtonSx,
  viewerPanelSurfaceSx,
} from "./unityWebViewer/panelStyles";
import { useLocalization } from "../localization/LocalizationContext";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const normalizeState = (s) => {
  const normalizedState = normalizeGuideStateValue(s);
  if (!normalizedState) return "DEFAULT";
  const up = String(normalizedState).toUpperCase();
  if (up.includes("LISTEN")) return "LISTENING";
  if (up.includes("TALK") || up.includes("SPEAK")) return "TALKING";
  if (up.includes("THINK") || up.includes("PROCESS")) return "THINKING";
  if (
    up.includes("WAIT") ||
    up.includes("PAUSE") ||
    up.includes("IDLE") ||
    up.includes("INSIGHT_COMPLETED") ||
    up.includes("INSIGHT_CANCELLED") ||
    up.includes("GUIDE_STARTED") ||
    up.includes("INSIGHT_STARTED")
  ) {
    return "WAITING";
  }
  if (up.includes("READY")) return "DEFAULT";
  return "DEFAULT";
};

const SUBTITLE_MIN_WORDS = 10;
const SUBTITLE_TARGET_WORDS = 15;
const SUBTITLE_MAX_WORDS = 15;
const ESTIMATED_WORD_MS = 360;
const ESTIMATED_CHAR_MS = 24;
const MIN_SUBTITLE_CHUNK_MS = 1800;
const MAX_SUBTITLE_CHUNK_MS = 7000;
const SUBTITLE_END_PAUSE_MS = 650;

const splitIntoSubtitleChunks = (text) => {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return [];
  if (words.length <= SUBTITLE_MAX_WORDS) return [words.join(" ")];

  const chunks = [];
  let current = [];

  words.forEach((word) => {
    current.push(word);

    const endsPhrase = /[.!?;:]$/.test(word);
    const shouldEndAtPunctuation =
      current.length >= SUBTITLE_MIN_WORDS && endsPhrase;
    const shouldEndAtTarget = current.length >= SUBTITLE_TARGET_WORDS;
    const shouldEndAtMax = current.length >= SUBTITLE_MAX_WORDS;

    if (shouldEndAtPunctuation || shouldEndAtTarget || shouldEndAtMax) {
      chunks.push(current);
      current = [];
    }
  });

  if (current.length > 0) {
    chunks.push(current);
  }

  if (chunks.length > 1) {
    const last = chunks[chunks.length - 1];
    const previous = chunks[chunks.length - 2];

    while (
      last.length < SUBTITLE_MIN_WORDS &&
      previous.length > SUBTITLE_MIN_WORDS
    ) {
      last.unshift(previous.pop());
    }

    if (
      last.length < SUBTITLE_MIN_WORDS &&
      previous.length + last.length <= SUBTITLE_MAX_WORDS
    ) {
      chunks.splice(chunks.length - 2, 2, [...previous, ...last]);
    }
  }

  return chunks.map((chunk) => chunk.join(" "));
};

const estimateSubtitleChunkWeight = (chunk) => {
  const words = String(chunk || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const characterCount = words.join("").length;
  return Math.max(words.length * ESTIMATED_WORD_MS, characterCount * ESTIMATED_CHAR_MS, 1);
};

const estimateSubtitleChunkDuration = (chunk, isLastChunk = false) => {
  const estimatedMs = estimateSubtitleChunkWeight(chunk);

  const clampedMs = Math.min(
    Math.max(estimatedMs, MIN_SUBTITLE_CHUNK_MS),
    MAX_SUBTITLE_CHUNK_MS,
  );

  return isLastChunk ? clampedMs + SUBTITLE_END_PAUSE_MS : clampedMs;
};

const getSubtitleChunkDurations = (chunks, audioLengthSeconds) => {
  const audioLengthMs = Number(audioLengthSeconds) * 1000;
  if (Number.isFinite(audioLengthMs) && audioLengthMs > 0 && chunks.length > 0) {
    const weights = chunks.map(estimateSubtitleChunkWeight);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

    if (totalWeight > 0) {
      return weights.map((weight) => Math.max(50, (audioLengthMs * weight) / totalWeight));
    }
  }

  return chunks.map((chunk, index) =>
    estimateSubtitleChunkDuration(chunk, index === chunks.length - 1),
  );
};

const useTimedSubtitleChunks = (text, isActive, audioLengthSeconds = 0) => {
  const chunks = useMemo(() => {
    if (!text) return [];
    return splitIntoSubtitleChunks(text);
  }, [text]);

  const chunkDurations = useMemo(
    () => getSubtitleChunkDurations(chunks, audioLengthSeconds),
    [audioLengthSeconds, chunks],
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (!isActive) return undefined;

    setCurrentIndex(0);

    if (chunks.length <= 1) return undefined;

    let timeoutId = null;

    const scheduleNextChunk = (index) => {
      if (index >= chunks.length - 1) return;

      timeoutId = setTimeout(() => {
        const nextIndex = index + 1;
        setCurrentIndex(nextIndex);
        scheduleNextChunk(nextIndex);
      }, chunkDurations[index]);
    };

    scheduleNextChunk(0);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isActive, chunks.length, chunkDurations]);

  if (!text || chunks.length === 0) {
    return { displayText: "", progress: 0, sentenceCount: 0, currentSentence: 0 };
  }

  const safeIndex = Math.min(currentIndex, chunks.length - 1);
  const displayText = chunks[safeIndex] || chunks[0];
  const progress = chunks.length > 1 ? (safeIndex / (chunks.length - 1)) * 100 : 100;

  return { displayText, progress, sentenceCount: chunks.length, currentSentence: safeIndex + 1 };
};

// ============================================
// ORB
// ============================================

const ORB_SIZE = 131;
const ORB_STAGE_HEIGHT = 141;
const ORB_HOVER_TOP_OFFSET = 26;
const ORB_STAGE_LIFT = 18;
const QUESTION_BADGE_SIZE = 44;
const QUESTION_BADGE_HOVER_WIDTH = 154;
// Keep the Assistant API implementation available, but hide its viewer entry point.
const SHOW_ASK_GUIDE_QUESTION_ENTRYPOINT = false;

const ReplayGuideIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M12 5a7 7 0 1 1-6.32 10h2.24A5 5 0 1 0 12 7H8.8l2.1 2.1-1.42 1.42L4.96 6l4.52-4.52L10.9 2.9 8.8 5H12Z"
      fill="currentColor"
    />
  </SvgIcon>
);

const getOrbBadgeSx = (isExpanded, hoverWidth = QUESTION_BADGE_HOVER_WIDTH) => ({
  ...viewerPillActionButtonSx,
  width: isExpanded ? hoverWidth : QUESTION_BADGE_SIZE,
  height: QUESTION_BADGE_SIZE,
  px: isExpanded ? viewerCompactActionPaddingX : 0,
  m: 0,
  minHeight: QUESTION_BADGE_SIZE,
  minWidth: 0,
  transform: "translate(-50%, -42%)",
  zIndex: 3,
  animation: "questionBadgePop 260ms cubic-bezier(0.2, 0.9, 0.2, 1)",
  overflow: "hidden",
  transition:
    "top 180ms ease, width 180ms ease, padding 180ms ease, background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease",
  "&:hover": {
    ...viewerPillActionButtonSx["&:hover"],
    transform: "translate(-50%, -42%)",
  },
  "&:active": {
    ...viewerPillActionButtonSx["&:active"],
    transform: "translate(-50%, -42%)",
  },
  "@keyframes questionBadgePop": {
    "0%": {
      opacity: 0,
      transform: "translate(-50%, -18%) scale(0.78)",
    },
    "100%": {
      opacity: 1,
      transform: "translate(-50%, -42%) scale(1)",
    },
  },
  "&::after": {
    content: "none",
  },
});

// ============================================
// BLOB PROTOTYPE
// ============================================

const BlobPrototype = ({
  guideState,
  guideStateText,
  onStartGuide,
  onRepeatInsight,
  isRecording = false,
  onStopRecording,
  questionState = "idle",
  questionEnabled = false,
  questionDisplayText = "",
  onAskQuestion,
  microphonePermissionDenied = false,
  onEnableMicrophone,
  defaultMessage = "",
  showStartGuideButton = false,
  guideAudioLevel = 0,
  microphoneLevel = 0,
}) => {
  const { t } = useLocalization();
  const guideVisualState = normalizeState(guideState?.guideState);
  const normalizedGuideState = normalizeGuideStateValue(guideState?.guideState);
  const state =
    questionState === "recording"
      ? "LISTENING"
      : questionState === "processing"
      ? "THINKING"
      : questionState === "speaking"
      ? "TALKING"
      : questionState === "error"
      ? "WAITING"
      : guideVisualState;
  const guideStateMessage =
    typeof guideState?.script === "string"
      ? guideState.script.trim()
      : typeof guideState?.data === "string"
      ? guideState.data.trim()
      : "";
  const guideTextMessage =
    typeof guideStateText === "string" ? guideStateText.trim() : "";
  const message = guideStateMessage || guideTextMessage || "";
  const hasExplicitEmptyScript =
    guideState &&
    Object.prototype.hasOwnProperty.call(guideState, "script") &&
    !guideStateMessage;
  const [rememberedGuideMessage, setRememberedGuideMessage] = useState("");

  useEffect(() => {
    if (message) {
      setRememberedGuideMessage(message);
    } else if (hasExplicitEmptyScript) {
      setRememberedGuideMessage("");
    }
  }, [hasExplicitEmptyScript, message]);

  const persistentGuideMessage = hasExplicitEmptyScript
    ? ""
    : message || rememberedGuideMessage;
  const { displayText } = useTimedSubtitleChunks(
    persistentGuideMessage,
    state === "TALKING",
    guideState?.audioLength,
  );
  const visibleGuideMessage = displayText || persistentGuideMessage;
  const stateColor =
    GUIDE_VISUAL_STATES[state]?.hex || GUIDE_VISUAL_STATES.DEFAULT.hex;
  const stateRgb = hexToRgbGuide(stateColor);
  const [showWaitingHint, setShowWaitingHint] = useState(false);
  const [showQuestionHoverHint, setShowQuestionHoverHint] = useState(false);

  const showQuestionRecordingUI = questionState === "recording";
  const showRecordingUI = state === "LISTENING" && (isRecording || showQuestionRecordingUI);
  const questionEntryPointEnabled =
    SHOW_ASK_GUIDE_QUESTION_ENTRYPOINT && questionEnabled;
  const canStartGuide = Boolean(showStartGuideButton && onStartGuide);
  const showStartGuideInlineButton =
    !showRecordingUI &&
    canStartGuide &&
    (questionState === "idle" || questionState === "error");
  const orbActionEnabled =
    !showRecordingUI &&
    !showStartGuideInlineButton &&
    questionEntryPointEnabled &&
    (questionState === "idle" || questionState === "error");
  const showQuestionTapBadge =
    !showRecordingUI &&
    !showStartGuideInlineButton &&
    questionEntryPointEnabled &&
    (questionState === "idle" || questionState === "error");
  const insightReplayAvailable = [
    "INSIGHT_STARTED",
    "TALKING",
    "LISTENING",
    "THINKING",
    "WAITING",
  ].some((stateName) => normalizedGuideState.includes(stateName));
  const canRepeatInsight = Boolean(
    !showStartGuideButton && guideState?.isRecovery !== true && onRepeatInsight && insightReplayAvailable,
  );
  const showGuideActionButton =
    !showRecordingUI &&
    canRepeatInsight &&
    (questionState === "idle" || questionState === "error") &&
    !normalizedGuideState.includes("INSIGHT_COMPLETED") &&
    !normalizedGuideState.includes("INSIGHT_CANCELLED");
  const showMicrophonePermissionButton =
    !showRecordingUI &&
    microphonePermissionDenied &&
    typeof onEnableMicrophone === "function";

  useEffect(() => {
    if (guideVisualState !== "WAITING") {
      setShowWaitingHint(false);
      return undefined;
    }

    const timerId = setTimeout(() => {
      setShowWaitingHint(true);
    }, 30000);

    return () => clearTimeout(timerId);
  }, [guideVisualState]);

  useEffect(() => {
    if (!showQuestionTapBadge) {
      setShowQuestionHoverHint(false);
    }
  }, [showQuestionTapBadge]);

  const handleQuestionClick = () => {
    if (
      questionEntryPointEnabled &&
      (questionState === "idle" || questionState === "error") &&
      onAskQuestion
    ) {
      onAskQuestion();
    }
  };

  const handleQuestionBadgeClick = (event) => {
    event.stopPropagation();
    handleQuestionClick();
  };

  const handleGuideActionBadgeClick = (event) => {
    event.stopPropagation();
    onRepeatInsight();
  };

  const handleStartGuideButtonClick = (event) => {
    event.stopPropagation();
    onStartGuide();
  };

  const handleOrbClick = (event) => {
    if (!orbActionEnabled) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - bounds.left;
    const clickY = event.clientY - bounds.top;
    const orbRadius = ORB_SIZE / 2;
    const orbCenterX = bounds.width / 2;
    const orbCenterY = ORB_HOVER_TOP_OFFSET + orbRadius;
    const distanceFromCenter = Math.hypot(clickX - orbCenterX, clickY - orbCenterY);

    if (distanceFromCenter > orbRadius) {
      return;
    }

    handleQuestionClick();
  };

  const getDisplayMessage = () => {
    if (questionState === "recording") return t("guide.ask_hint");
    if (questionState === "processing") return t("guide.thinking_question");
    if (questionState === "speaking" && questionDisplayText) return questionDisplayText;
    if (questionState === "error" && questionDisplayText) return questionDisplayText;
    if (state === "TALKING" && displayText) return displayText;
    if (microphonePermissionDenied && state === "LISTENING") {
      return visibleGuideMessage || defaultMessage;
    }
    if (state === "LISTENING") return visibleGuideMessage || t("guide.listening_short");
    if (state === "THINKING") return t("guide.thinking");
    if (state === "WAITING") return visibleGuideMessage || defaultMessage;
    return visibleGuideMessage || defaultMessage;
  };
  const displayMessage = showStartGuideInlineButton ? "" : getDisplayMessage();
  const hasDisplayMessage = Boolean(displayMessage.trim());
  const blobAudioLevel =
    state === "LISTENING"
      ? microphoneLevel
      : state === "TALKING"
      ? guideAudioLevel
      : 0;
  const guideActionLabel = t("guide.replay");

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        px: 0,
        py: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 0.75,
        pointerEvents: "auto",
        position: "relative",
      }}
    >
      {/* Orb */}
      <Box
        onClick={handleOrbClick}
        onMouseEnter={showQuestionTapBadge ? () => setShowQuestionHoverHint(true) : undefined}
        onMouseLeave={showQuestionTapBadge ? () => setShowQuestionHoverHint(false) : undefined}
        sx={{
          mb: showGuideActionButton ? 0 : 0.35,
          height: ORB_STAGE_HEIGHT,
          width: "100%",
          maxWidth: "min(280px, 100%)",
          mt: `-${ORB_HOVER_TOP_OFFSET + ORB_STAGE_LIFT}px`,
          pt: `${ORB_HOVER_TOP_OFFSET}px`,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          position: "relative",
          overflow: "visible",
          cursor: orbActionEnabled ? "pointer" : "default",
        }}
      >
        {showQuestionTapBadge && (
          <Box
            component="button"
            type="button"
            aria-label={t("guide.ask")}
            onClick={handleQuestionBadgeClick}
            onFocus={() => setShowQuestionHoverHint(true)}
            onBlur={() => setShowQuestionHoverHint(false)}
            sx={{
              position: "absolute",
              top: showQuestionHoverHint ? 0 : 8,
              left: "50%",
              ...getOrbBadgeSx(showQuestionHoverHint),
            }}
          >
            <Typography
              component="span"
              className="control-label"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: showQuestionHoverHint ? "0.82rem" : "1.2rem",
                fontWeight: "var(--viewer-action-font-weight, 700)",
                lineHeight: 1,
                letterSpacing: 0,
                whiteSpace: "nowrap",
                transition: "font-size 180ms ease, transform 180ms ease",
              }}
            >
              {showQuestionHoverHint ? t("guide.ask") : "?"}
            </Typography>
          </Box>
        )}

        <GuideWaveformOrb
          state={state}
          audioLevel={blobAudioLevel}
          size={ORB_SIZE}
        />

      </Box>

      {showGuideActionButton && (
        <Box
          component="button"
          type="button"
          aria-label={guideActionLabel}
          onClick={handleGuideActionBadgeClick}
          sx={{
            ...viewerPillActionButtonSx,
            mt: "var(--guide-orb-replay-gap, 24px)",
            px: viewerCompactActionPaddingX,
            gap: "4px",
            "& svg": {
              ...viewerPillActionButtonSx["& .MuiSvgIcon-root, & svg"],
              fontSize: 16,
            },
          }}
        >
          <ReplayGuideIcon />
          <Typography
            component="span"
            className="control-label"
            sx={{
              ...viewerActionButtonLabelSx,
              fontSize: "13px",
              fontWeight: "var(--viewer-action-font-weight, 700)",
              letterSpacing: 0,
              whiteSpace: "nowrap",
            }}
          >
            {t("guide.replay")}
          </Typography>
        </Box>
      )}

      {showStartGuideInlineButton && (
        <Box
          component="button"
          type="button"
          aria-label={t("guide.start")}
          onClick={handleStartGuideButtonClick}
          sx={{
            ...viewerPillActionButtonSx,
            mt: 0.35,
            mb: hasDisplayMessage ? 0.75 : 0,
            px: viewerCompactActionPaddingX,
          }}
        >
          <Typography
            component="span"
            className="control-label"
            sx={{
              ...viewerActionButtonLabelSx,
              fontSize: "13px",
              fontWeight: "var(--viewer-action-font-weight, 700)",
              letterSpacing: 0,
              whiteSpace: "nowrap",
            }}
          >
            {t("guide.start")}
          </Typography>
        </Box>
      )}

      {hasDisplayMessage && (
        <Box
          sx={{
            order: -1,
            width: "fit-content",
            maxWidth: "min(var(--guide-message-max-width, 320px), 100%)",
            boxSizing: "border-box",
            ...getViewerPanelPaddingSx(false),
            // Compensate for the lifted orb stage so the text clears the orb.
            mb: `calc(${ORB_STAGE_LIFT}px + var(--guide-orb-text-gap, 4px))`,
            ...viewerPanelSurfaceSx,
            color: "var(--label-text, #f2fffc)",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              lineHeight: 1.68,
              color: "var(--label-text, #f2fffc)",
              textAlign: "center",
              width: "auto",
            }}
          >
            {displayMessage}
          </Typography>
        </Box>
      )}

      {showRecordingUI && (
        <Box
          sx={{
            width: "100%",
            maxWidth: "min(280px, 100%)",
            boxSizing: "border-box",
            mt: hasDisplayMessage ? 0.75 : 0,
            ...getViewerPanelPaddingSx(false),
            ...viewerPanelSurfaceSx,
            color: "var(--label-text, #f2fffc)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.9,
            animation: "recFadeIn 0.2s ease",
            "@keyframes recFadeIn": {
              "0%":   { opacity: 0 },
              "100%": { opacity: 1 },
            },
          }}
        >
          {/* Row: mic dot + "Recording" label + waveform bars */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              "@keyframes recordBar": {
                "0%, 100%": { transform: "scaleY(0.35)", opacity: 0.45 },
                "50%":      { transform: "scaleY(1)",    opacity: 1    },
              },
            }}
          >
            {/* Pulsing mic dot */}
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.9)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "@keyframes micRing": {
                  "0%":   { boxShadow: `0 0 0 0   rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.45)` },
                  "70%":  { boxShadow: `0 0 0 6px rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0)`    },
                  "100%": { boxShadow: `0 0 0 0   rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0)`    },
                },
                animation: "micRing 1.4s ease-out infinite",
              }}
            >
              <MicrophoneIcon width={12} height={12} />
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                color: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.95)`,
              }}
            >
              {t("guide.recording")}
            </Typography>

            {/* Waveform bars */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 3,
                    height: 11,
                    borderRadius: "999px",
                    background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.8)`,
                    animation: "recordBar 0.85s ease-in-out infinite",
                    animationDelay: `${i * 0.13}s`,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* I'm done button */}
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onStopRecording();
            }}
            sx={{
              ...viewerActionButtonSx,
              width: "100%",
              px: getViewerActionPaddingX(false),
              fontSize: "13px",
            }}
          >
            <Typography
              component="span"
              className="control-label"
              sx={{
                ...viewerActionButtonLabelSx,
                fontSize: "13px",
              }}
            >
              {t("guide.recording_done")}
            </Typography>
          </Box>
        </Box>
      )}

      {showMicrophonePermissionButton && (
        <Box
          component="button"
          type="button"
          aria-label={t("guide.enable_microphone")}
          onClick={(event) => {
            event.stopPropagation();
            onEnableMicrophone();
          }}
          sx={{
            ...viewerGhostActionButtonSx,
            mt: 0.75,
            px: viewerCompactActionPaddingX,
            border: `1px solid rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.42)`,
            background: "rgba(0,0,0,0.42)",
            color: "#fff",
            gap: 0.6,
            boxShadow: `0 8px 18px rgba(0,0,0,0.24), 0 0 14px rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.16)`,
            "& svg": {
              ...viewerGhostActionButtonSx["& .MuiSvgIcon-root, & svg"],
              width: 14,
              height: 14,
            },
            "&:hover": {
              background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.18)`,
              borderColor: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.65)`,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(1px)",
            },
            "&:focus-visible": {
              outline: `2px solid rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.9)`,
              outlineOffset: 3,
            },
          }}
        >
          <MicrophoneIcon />
          <Typography
            component="span"
            className="control-label"
            sx={{
              ...viewerActionButtonLabelSx,
              fontSize: "12px",
              fontWeight: "var(--viewer-action-font-weight, 700)",
              color: "rgba(255,255,255,0.96)",
            }}
          >
            {t("guide.enable_microphone")}
          </Typography>
        </Box>
      )}


      {!showRecordingUI && questionEntryPointEnabled && questionState === "error" && (
        <Typography
          sx={{
            mt: 0.65,
            fontSize: "0.8rem",
            color: "rgb(255, 255, 255)",
            letterSpacing: "0.01em",
            textAlign: "center",
          }}
        >
          {t("guide.retry_hint")}
        </Typography>
      )}

      {!showRecordingUI && !questionEnabled && showWaitingHint && (
        <Typography
          sx={{
            mt: 0.65,
            fontSize: "0.8rem",
            color: "rgb(255, 255, 255)",
            letterSpacing: "0.01em",
            textAlign: "center",
          }}
        >
          {t("guide.replay_hint")}
        </Typography>
      )}

      {/* Use the bottom reserve for Replay so extra orb spacing lowers the button. */}
      <Box sx={{ height: showGuideActionButton ? 0 : 18, mt: showGuideActionButton ? 0 : 1 }} />
    </Box>
  );
};

// ============================================
// WRAPPER
// ============================================

const GuidePrototypes = ({
  guideState,
  guideStateText,
  startGuide,
  repeatInsight,
  isRecording = false,
  onStopRecording,
  questionState = "idle",
  questionEnabled = false,
  questionDisplayText = "",
  onAskQuestion,
  microphonePermissionDenied = false,
  onEnableMicrophone,
  defaultMessage = "",
  showStartGuideButton = false,
  guideAudioLevel = 0,
  microphoneLevel = 0,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        pointerEvents: "auto",
        minWidth: 0,
      }}
    >
      <BlobPrototype
        guideState={guideState}
        guideStateText={guideStateText}
        onStartGuide={startGuide}
        onRepeatInsight={repeatInsight}
        isRecording={isRecording}
        onStopRecording={onStopRecording}
        questionState={questionState}
        questionEnabled={questionEnabled}
        questionDisplayText={questionDisplayText}
        onAskQuestion={onAskQuestion}
        microphonePermissionDenied={microphonePermissionDenied}
        onEnableMicrophone={onEnableMicrophone}
        defaultMessage={defaultMessage}
        showStartGuideButton={showStartGuideButton}
        guideAudioLevel={guideAudioLevel}
        microphoneLevel={microphoneLevel}
      />
    </Box>
  );
};

export default GuidePrototypes;
export { BlobPrototype };
